import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  defaultCheckoutPlan,
  fetchPublicPlans,
  sortPlans,
  type PublicPlan,
} from '@/content/plans';
import { buildPlans } from '@/seo/catalog';

interface PlansContextValue {
  plans: PublicPlan[];
  sortedPlans: PublicPlan[];
  defaultPlan: PublicPlan | undefined;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const PlansContext = createContext<PlansContextValue | null>(null);

export function PlansProvider({ children }: { children: ReactNode }) {
  // Nasce com os planos do build: o HTML gerado já mostra preço real e o React
  // assume a página com os mesmos valores. A busca ao vivo atualiza por cima.
  const [plans, setPlans] = useState<PublicPlan[]>(buildPlans);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    fetchPublicPlans()
      .then((data) => {
        if (!cancelled && data.length > 0) {
          setPlans(data);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          // Mantém os planos do build em vez de esvaziar a página de preços.
          setError(err instanceof Error ? err.message : 'Falha ao carregar planos');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const sortedPlans = useMemo(() => sortPlans(plans), [plans]);
  const defaultPlan = useMemo(() => defaultCheckoutPlan(plans), [plans]);

  const value = useMemo(
    () => ({
      plans,
      sortedPlans,
      defaultPlan,
      loading,
      error,
      reload: () => setTick((n) => n + 1),
    }),
    [plans, sortedPlans, defaultPlan, loading, error],
  );

  return <PlansContext.Provider value={value}>{children}</PlansContext.Provider>;
}

export function usePlansCatalog(): PlansContextValue {
  const ctx = useContext(PlansContext);
  if (!ctx) {
    throw new Error('usePlansCatalog deve ser usado dentro de PlansProvider');
  }
  return ctx;
}
